import { Component } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { NgxSpinnerService } from 'ngx-spinner';
import { ToastrService } from 'ngx-toastr';
import { HttpService } from 'src/app/https.service';
import { LeadsService } from 'src/app/service/lead.service';
import { URLUtils } from 'src/app/urlUtils';
import * as jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import AutoTableOutput from "jspdf-autotable";

@Component({
    selector: 'app-review-details',
    templateUrl: './review-details.component.html',
    styleUrls: ['./review-details.component.css'],
    standalone: false
})
export class ReviewDetailsComponent {
lead: any;
  currentDate: Date = new Date();
  selectedFields: string[] = [];
  downloadData: any = {};
  editMode: boolean = false;
  reviewForm!: FormGroup;
  uid: any;
  leadId: string = '';
  isGeneratingPDF: boolean = false;
  firmname:any;

  // Field labels mapping
  fieldLabels = {
    name: 'Name',
    email: 'Email',
    phone: 'Phone',
    department: 'Department',
    status: 'Status',
    issue: 'Issue',
    created: 'Created On'
  };

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private leadsService: LeadsService,
    private httpService: HttpService,
    private spinner: NgxSpinnerService,
    private fb: FormBuilder,
    private modalService: NgbModal,
    private toastr: ToastrService
  ) {
    // Get data from router state
    const navigation = this.router.getCurrentNavigation();
    if (navigation?.extras.state) {
      this.downloadData = navigation.extras.state['data'] || {};
      this.selectedFields = navigation.extras.state['fields'] || [];
      this.lead = navigation.extras.state['lead'] || {};
    }
  }

  ngOnInit(): void {
    this.firmname = localStorage.getItem('firm_name');
    this.initializeForm();
    
    // If no data from router state, try to get from service
    if (!this.lead || Object.keys(this.lead).length === 0) {
      this.leadsService.currentLead.subscribe(lead => {
        if (lead) {
          this.lead = lead;
          this.populateDownloadData();
          this.patchFormValues();
        }
      });
    } else {
      this.patchFormValues();
    }

    this.route.params.subscribe(params => {
      this.leadId = params['id'];
    });
  }

  initializeForm() {
    const formControls: any = {
      message: ['', [Validators.maxLength(250)]]
    };

    // Add form controls for selected fields
    this.selectedFields.forEach(field => {
      switch (field) {
        case 'name':
          formControls[field] = ['', Validators.required];
          break;
        case 'email':
          formControls[field] = ['', [Validators.required, Validators.email]];
          break;
        case 'phone':
          formControls[field] = ['', Validators.pattern('^[0-9]{10}$')];
          break;
        default:
          formControls[field] = [''];
          break;
      }
    });

    this.reviewForm = this.fb.group(formControls);
  }

  populateDownloadData() {
    if (this.lead && this.selectedFields.length > 0) {
      this.downloadData = {};
      this.selectedFields.forEach(field => {
        if (this.lead[field]) {
          this.downloadData[field] = this.lead[field];
        }
      });
    }
  }

  patchFormValues() {
    if (this.lead && this.selectedFields.length > 0) {
      const patchData: any = {
        message: ''
      };

      this.selectedFields.forEach(field => {
        if (this.lead[field]) {
          patchData[field] = this.lead[field];
        }
      });

      this.reviewForm.patchValue(patchData);
    }
  }

  toggleEditMode() {
    this.editMode = !this.editMode;
    if (!this.editMode) {
      this.patchFormValues(); // Reset form if cancel editing
    }
  }

  onBack() {
    // Navigate back to download-details component
    this.router.navigate(['/download-details', this.leadId]);
  }

  onCancel() {
    if (this.editMode) {
      this.editMode = false;
      this.patchFormValues(); // Reset form
      this.toastr.info('Changes discarded');
    } else {
      // Navigate back to leads list or previous page
      this.router.navigate(['/leads']);
    }
  }

  onSaveAsPDF() {
    // Show confirmation dialog
    const confirmed = window.confirm('Are you sure you want to download this information?');
    
    if (confirmed) {
      this.generatePDF();
    }
  }

 async generatePDF() {
  this.isGeneratingPDF = true;
  this.spinner.show();

  try {
    const doc = new jsPDF.jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    const logoUrl = 'assets/logobg.png'; // your local asset path
    const img = await this.loadImage(logoUrl);

    let yPosition = 20; // Initial Y position for content
   const logoWidth = 40;
   const logoHeight = 20;

    // Add text on the left
    doc.setFontSize(18);
    doc.setFont('helvetica', 'bold');
    doc.text(`Need Counsel`, 20, yPosition + 15);

    // Add logo on the right
    doc.addImage(img, 'PNG', doc.internal.pageSize.getWidth() - logoWidth - 20, yPosition, logoWidth, logoHeight);

    yPosition += 35;

    // 3. Table of selected fields
    const tableData = this.selectedFields.map(field => [
      this.getFieldLabel(field),
      this.getFieldValue(field) || 'N/A',
    ]);

    autoTable(doc, {
      startY: yPosition,
      head: [['Field', 'Value']],
      body: tableData,
      theme: 'grid',
      headStyles: { fillColor: [41, 128, 185], textColor: 255 },
      styles: { fontSize: 11 },
    });

    const finalY = (doc as any).lastAutoTable?.finalY || yPosition + 20;

    // Additional Message
    const message = this.reviewForm.get('message')?.value;
    if (message) {
      doc.setFontSize(14);
      doc.setFont('helvetica', 'bold');
      doc.text('Additional Information:', 20, finalY + 10);

      doc.setFont('helvetica', 'normal');
      doc.setFontSize(11);
      const splitMessage = doc.splitTextToSize(message, pageWidth - 40);
      doc.text(splitMessage, 20, finalY + 18);
    }

    // Footer
    doc.setFontSize(10);
    doc.setTextColor(150);
    doc.text('Powered By DigiCoffer Inc', 20, doc.internal.pageSize.height - 10);

    // Save PDF
    doc.save(`lead-details-${this.lead?.name || 'unknown'}-${Date.now()}.pdf`);

  } catch (error) {
    this.toastr.error('Failed to Generate PDF!');
    console.error('PDF generation error:', error);
  } finally {
    this.toastr.success('PDF Generated Successfully');
    this.isGeneratingPDF = false;
    this.spinner.hide();
  }
}

  private loadImage(url: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.src = url;
    img.onload = () => resolve(img);
    img.onerror = (err) => reject(err);
  });
}








  saveChanges() {
    if (this.reviewForm.invalid) {
      this.markFormGroupTouched(this.reviewForm);
      this.toastr.warning('Please fix the form errors before saving');
      return;
    }

    const formValues = this.reviewForm.value;
    const payload: any = {
      id: this.lead.id
    };

    // Add only the selected fields to payload
    this.selectedFields.forEach(field => {
      if (formValues[field] !== undefined) {
        payload[field] = formValues[field];
      }
    });

    this.spinner.show();
    this.uid = localStorage.getItem('profuid');

    this.httpService.sendRePatchRequest(URLUtils.updateQuery(this.lead.id, this.uid), payload).subscribe({
      next: (res) => {
        this.spinner.hide();
        if (res) {
          this.toggleEditMode();
          this.toastr.success('Details updated successfully');
          
          // Update the lead data
          this.selectedFields.forEach(field => {
            if (this.lead[field] !== undefined) {
              this.lead[field] = formValues[field];
            }
          });
        }
      },
      error: (error) => {
        this.spinner.hide();
        this.toastr.error('Failed to update details');
        console.error('Update error:', error);
      }
    });
  }

  canceledit() {
  this.editMode = false;
  // Optionally, reset the form or revert changes here if needed
  }

  markFormGroupTouched(formGroup: FormGroup) {
    Object.values(formGroup.controls).forEach(control => {
      control.markAsTouched();
      if ((control as any).controls) {
        this.markFormGroupTouched(control as FormGroup);
      }
    });
  }

  // Getter for message character count
  get messageCharCount(): number {
    return this.reviewForm.get('message')?.value?.length || 0;
  }

  // Check if field should be displayed
  isFieldSelected(field: string): boolean {
    return this.selectedFields.includes(field);
  }

  // Get field label
  getFieldLabel(field: string): string {
    return this.fieldLabels[field as keyof typeof this.fieldLabels] || field;
  }

  // Get field value for display
  getFieldValue(field: string): any {
    if (this.editMode) {
      return this.reviewForm.get(field)?.value;
    }
    return this.lead?.[field] || this.downloadData?.[field] || '';
  }

  // Check if field has validation errors
  hasFieldError(field: string): boolean {
    const control = this.reviewForm.get(field);
    return !!(control && control.invalid && control.touched);
  }

  // Get field error message
  getFieldError(field: string): string {
    const control = this.reviewForm.get(field);
    if (control && control.errors && control.touched) {
      if (control.errors['required']) {
        return `${this.getFieldLabel(field)} is required`;
      }
      if (control.errors['email']) {
        return 'Please enter a valid email address';
      }
      if (control.errors['pattern']) {
        return 'Please enter a valid phone number';
      }
    }
    return '';
  }
}
